import { motion, useReducedMotion } from 'motion/react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';

import {
  cardCopy,
  listNames,
  type CardKind,
  type CardStatus,
} from './tagged-people';

type TaggedPeopleCardProps = {
  kind: CardKind;
  /** The names of the people that the card is about */
  names: string[];
  status: CardStatus;
  onAction: () => void;
  onDismiss: () => void;
};

/** Asks whether to connect with, accept or invite the people tagged in the draft. It never blocks sending. */
export const TaggedPeopleCard = ({
  kind,
  names,
  status,
  onAction,
  onDismiss,
}: TaggedPeopleCardProps) => {
  const shouldReduceMotion = useReducedMotion();
  const { title, description, actionLabel, doneLabel } = cardCopy(kind, names);
  const isDone = status === 'done';
  const Icon = isDone ? Icons.check : kind === 'invite' ? Icons.mail : null;

  return (
    <motion.div
      data-tagged-people-card=""
      initial={shouldReduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.18 }}
      className="mb-2 overflow-hidden rounded-xl border border-border-strong bg-surface"
    >
      <XStack className="items-start gap-2 py-2 pl-3 pr-2">
        <span className="mt-1 flex size-3.5 flex-shrink-0 items-center justify-center text-foreground-muted">
          {Icon ? (
            <Icon className="size-3.5" />
          ) : (
            <AppLogo size={14} customColor="currentColor" decorative />
          )}
        </span>
        <YStack className="min-w-0 flex-1 text-sm text-foreground-muted">
          {!isDone && (
            <span>
              <span className="font-medium text-foreground">{title}</span>
              {description && ` ${description}`}
            </span>
          )}
          {/* Present from the start, because a screen reader announces the text that joins a region, not a new region */}
          <span role="status" aria-live="polite" className="text-foreground">
            {isDone && doneLabel}
          </span>
        </YStack>
        {!isDone && (
          <Button
            size="sm"
            variant="outline"
            isLoading={status === 'sending'}
            className="flex-shrink-0"
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        )}
        <IconButton
          icon={Icons.cancel}
          size="xs"
          variant="ghost"
          tooltip="Dismiss"
          aria-label={`Don't ask about ${listNames(names)} in this chat`}
          onClick={onDismiss}
        />
      </XStack>
    </motion.div>
  );
};
